/**
 * M7 S38 part 2: the worker facts the story link reads, cached from the strategic client (`storyView()` and the
 * relations feed). Refreshed on init, on every online day and on every story result, never per frame.
 *
 * - **Stage** (D-425 variants, `StoryUiDeps.stage`): `corporation` once the worker reports a founded corporation,
 *   else `owned` / `leased` from the Career's lease.
 * - **World input** (`StoryServiceDeps.input`): the worker's faction day, the Tethys rig count and the stage.
 *   The worker reports story rigs but no AI rig count; Tethys's AI tetherstone income over the last ten faction
 *   days stands in for the AI rigs (income > 0 = one AI rig working), so Okafor's trust test asks for a mission rig
 *   only when no Tethys rig works at all.
 * - **Contact view** (`StoryUiDeps.contactView`, the claim's recipient): the worker's wanted levels and mirrored
 *   standings, and the corporation.
 */
import type { Career } from '../../career/types'
import type { ContactView } from '../../story/contacts'
import type { CareerStage } from '../../story/types'
import type { RelationsView, StoryView } from '../../strategic/types'
import type { WorldInput } from '../../story-link/world'

export type StoryViewFacts = Pick<StoryView, 'day' | 'corpFaction' | 'storyRigs' | 'tethysAiTetherIncome10d'>

/** The career stage now: the worker's corporation first, then the lease. */
export function stageOf(view: Pick<StoryView, 'corpFaction'> | null, career: Pick<Career, 'lease'> | null): CareerStage | null {
  if (view !== null && view.corpFaction !== null) return 'corporation'
  if (career === null) return null
  return career.lease.owned ? 'owned' : 'leased'
}

/** AI Tethys rigs working: one when Tethys earned AI tetherstone income in the window, else none. */
export function aiTethysRigs(view: Pick<StoryView, 'tethysAiTetherIncome10d'>): number {
  return view.tethysAiTetherIncome10d > 0 ? 1 : 0
}

/** The world input from the cached view (empty before the first read: the service's defaults apply). */
export function worldInputOf(view: StoryViewFacts | null, career: Pick<Career, 'lease'> | null): Partial<WorldInput> {
  if (view === null) return {}
  const stage = stageOf(view, career)
  const out: Partial<WorldInput> = { day: view.day, tethysRigs: view.storyRigs + aiTethysRigs(view) }
  if (stage !== null) out.stage = stage
  return out
}

/** The claim contact's view: the worker's wanted levels and standings, and the corporation. */
export function contactViewOf(view: Pick<StoryView, 'corpFaction'> | null, relations: RelationsView | null): ContactView {
  const out: ContactView = { wanted: relations?.player.wanted ?? {}, corpFaction: view?.corpFaction ?? null }
  if (relations !== null) out.standings = relations.player.standings
  return out
}

/** What the cache reads of the strategic client. */
export interface StoryViewClient {
  storyView(): Promise<StoryView>
  readonly relations: RelationsView | null
}

export interface StoryViewCache {
  view(): StoryView | null
  /** Re-reads `storyView()`; resolves once the new view is cached (a failed read keeps the old one). */
  refresh(): Promise<void>
  dispose(): void
}

export function createStoryViewCache(client: StoryViewClient): StoryViewCache {
  let view: StoryView | null = null
  let disposed = false
  let seq = 0
  return {
    view: () => view,
    async refresh(): Promise<void> {
      const mine = ++seq
      try {
        const v = await client.storyView()
        if (!disposed && mine === seq) view = v
      } catch {
        // The worker is gone or restarting: keep the last view (the service falls back to its defaults when null).
      }
    },
    dispose(): void {
      disposed = true
      view = null
    },
  }
}
